/**
 * "Check for package in Sourcepoint" — the Collateral Search action step.
 *
 * Backend: eastview-os run-agent-reviews.md "ACTION STEP — Check Sourcepoint for
 * package". Fires the Collateral Search agent
 * (`POST /admin/loans/{dealId}/ai-reviews/collateral-search/run`), which checks
 * whether this loan's recorded collateral is at Sourcepoint and imaged. It's a
 * BOTH-pipeline agent, so this action lives on both the preclose checklist and
 * the acquisitions Closed Package tab.
 *
 * Existence/imaging check only (Closed Package Review is what reviews contents).
 * Admin-only, matching the endpoint's require_admin.
 *
 * The full result (`packageIndexed`, `recordedInstruments`, `missing`, `source`)
 * is NOT persisted on the loan — it rides the `collateral_search_complete`
 * notification, and a "missing collateral" case also stages a finding. So this
 * is the trigger + last-run state; a persistent checklist panel would need the
 * backend to persist a result block (like `hardExceptionCheck`) first.
 */
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TravelExploreOutlined from "@mui/icons-material/TravelExploreOutlined";

import Txt from "../../../components/typography/Txt";
import { losAiReviewsApi } from "../../../api/losAiReviewsApi";
import { usePermissionsHook } from "../../../hooks/usePermissionsHook";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import colors from "../../../theme/colors";

const CollateralSearchAction = () => {
  const { isAdmin } = usePermissionsHook();
  const { enqueueSnackbar } = useSnackbar();
  const loan = useSelector((state) => state.loanDrilldown);
  const dealId = loan?._id;

  const [status, setStatus] = useState(null); // { lastRun, canRun, reason }
  const [running, setRunning] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!dealId) return;
    try {
      const data = await losAiReviewsApi.getStatus(dealId);
      setStatus(data?.collateral_search || null);
    } catch {
      // Endpoint unreachable / not live yet — leave unknown; the button still works.
    }
  }, [dealId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const hasRun = !!status?.lastRun;
  // Sourcepoint's imaging state changes independently of the loan, so a gated
  // "no changes since last run" shouldn't block a re-check — force it when gated.
  const forceIt = status?.canRun === false;

  const run = useCallback(async () => {
    if (!dealId || running) return;
    setRunning(true);
    try {
      const resp = await losAiReviewsApi.runCollateralSearch(
        dealId,
        forceIt ? { force: true } : {},
      );
      if (resp?.status === "skipped" || resp?.skipped) {
        enqueueSnackbar(
          resp?.message || "Collateral search skipped — no changes since the last check.",
          { variant: "info" },
        );
      } else {
        enqueueSnackbar(
          resp?.message ||
            "Checking Sourcepoint — you'll be notified when it finishes.",
          { variant: "success" },
        );
      }
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.detail ||
          err?.message ||
          "Could not start the collateral search.",
        { variant: "error" },
      );
    } finally {
      setRunning(false);
      fetchStatus();
    }
  }, [dealId, running, forceIt, enqueueSnackbar, fetchStatus]);

  if (!isAdmin || !dealId) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: "14px 18px",
        bgcolor: "#fff",
        border: "1px solid #e5e8ee",
        borderRadius: "12px",
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "9px",
          bgcolor: "#eff6ff",
          color: colors.action,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <TravelExploreOutlined sx={{ fontSize: 19 }} />
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Txt sx={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a" }}>
          Collateral at Sourcepoint
        </Txt>
        <Txt
          sx={{ fontSize: 12, color: "#64748b", display: "block", mt: "1px" }}
        >
          Confirm the recorded collateral is at the custodian and imaged.
          {hasRun && ` Last checked ${formatDateTime(status.lastRun)}.`}
        </Txt>
      </Box>

      <Button
        variant="outlined"
        size="small"
        disabled={running}
        onClick={run}
        startIcon={
          running ? (
            <CircularProgress size={13} thickness={5} sx={{ color: "inherit" }} />
          ) : undefined
        }
        sx={{ textTransform: "none", fontSize: 12.5, flexShrink: 0 }}
      >
        {running
          ? "Checking…"
          : hasRun
            ? "Check again"
            : "Check for package in Sourcepoint"}
      </Button>
    </Box>
  );
};

export default CollateralSearchAction;
